<div class="card">
  <div class="card-body py-4">
    <div
      class="flex flex-wrap items-center justify-between gap-2"
      [class.mb-4]="!collapsed"
    >
      <span class="card-title pb-1">
        <span
          class="inline-block h-6 w-6 cursor-pointer"
          (click)="collapsed = !collapsed"
        >
          @if (collapsed) {
            <app-chevron-right-icon></app-chevron-right-icon>
          } @else {
            <app-chevron-down-icon></app-chevron-down-icon>
          }
        </span>
        Activation Keys
      </span>
      <div [class.hidden]="collapsed" class="flex gap-2">
        <button
          type="button"
          class="btn btn-outline btn-sm"
          [disabled]="loading || !tenantId"
          (click)="load()"
        >
          @if (loading) {
            <span class="loading loading-spinner loading-sm"></span>
          }
          Refresh
        </button>
        <button
          type="button"
          class="btn btn-primary btn-sm"
          [disabled]="!tenantId"
          (click)="showCreateDialog()"
        >
          Create New Activation Key
        </button>
      </div>
    </div>

    <div [class.hidden]="collapsed">
      @if (error) {
        <p class="text-error">{{ error }}</p>
      } @else if (loading && items.length === 0) {
        <div class="skeleton h-32 w-full"></div>
      } @else if (items.length === 0) {
        <p>No one-off activation keys for this tenant.</p>
      } @else {
        <div class="overflow-x-auto rounded-lg border border-base-content/20">
          <table class="table">
            <thead>
              <tr>
                <th>Product code</th>
                <th>Product title</th>
                <th>Unique ID</th>
                <th>Expiration</th>
                <th>Activation Key</th>
                <th>Created</th>
                <th>Created by</th>
              </tr>
            </thead>
            <tbody>
              @for (item of items; track item.id) {
                <tr>
                  <td>{{ item.product_code }}</td>
                  <td>{{ item.product_title || '—' }}</td>
                  <td>{{ item.unique_id }}</td>
                  <td>
                    {{ item.expiration_date | date: 'MM/dd/yyyy' : 'UTC' }}
                  </td>
                  <td>
                    <div class="flex items-center gap-2">
                      @if (shownKeys.has(item.id)) {
                        <app-eye-slash-icon
                          (click)="toggleKey(item.id)"
                          class="inline-block h-4 w-4 cursor-pointer"
                          title="Hide activation key"
                        />
                      } @else {
                        <app-eye-icon
                          (click)="toggleKey(item.id)"
                          class="inline-block h-4 w-4 cursor-pointer"
                          title="Show activation key"
                        />
                      }
                      <app-file-copy-icon
                        (click)="copyKey(item, $event)"
                        class="inline-block h-4 w-4 cursor-pointer"
                        title="Copy activation key"
                      />
                      @if (shownKeys.has(item.id)) {
                        <span class="text-nowrap text-sm">{{
                          item.activation_key
                        }}</span>
                      } @else {
                        <span [innerHTML]="hiddenKeyString"></span>
                      }
                    </div>
                  </td>
                  <td>
                    {{ item.created_at | date: 'MM/dd/yyyy h:mm a' }}
                  </td>
                  <td>{{ item.created_by_email || '—' }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>
  </div>
</div>

<dialog #createDialog class="modal">
  <div class="modal-box max-w-xl">
    <h3 class="mb-4 text-lg font-bold">Create New Activation Key</h3>

    @if (createdKey) {
      <p class="mb-2">Activation key created.</p>
      <div class="mb-4 break-all rounded-md bg-base-200 p-3 font-mono text-sm">
        {{ createdKey.activation_key }}
      </div>
      <div class="modal-action">
        <button
          type="button"
          class="btn btn-outline"
          (click)="copyCreatedKey()"
        >
          @if (copied) {
            Copied
          } @else {
            Copy
          }
        </button>
        <button
          type="button"
          class="btn btn-primary"
          (click)="closeCreateDialog()"
        >
          Done
        </button>
      </div>
    } @else {
      @if (createError) {
        <p class="mb-3 text-error">{{ createError }}</p>
      }

      <div class="mb-4 flex gap-6">
        <label class="flex cursor-pointer items-center gap-2">
          <input
            type="radio"
            class="radio radio-sm"
            name="productSource"
            value="catalog"
            [(ngModel)]="productSource"
          />
          <span>Select product</span>
        </label>
        <label class="flex cursor-pointer items-center gap-2">
          <input
            type="radio"
            class="radio radio-sm"
            name="productSource"
            value="custom"
            [(ngModel)]="productSource"
          />
          <span>Custom 3-digit code</span>
        </label>
      </div>

      @if (productSource === 'catalog') {
        <div class="form-control mb-4">
          <div class="label">
            <span class="label-text">Product</span>
            <span class="label-text-alt">
              <label class="label cursor-pointer">
                <input
                  type="checkbox"
                  [(ngModel)]="showInactive"
                  (ngModelChange)="onShowInactiveChange()"
                  name="showInactive"
                  class="checkbox checkbox-sm"
                />
                <span for="showInactive" class="label-text pl-2"
                  >Show inactive products</span
                >
              </label>
            </span>
          </div>
          <input
            type="text"
            class="input input-bordered mb-2"
            placeholder="Search products..."
            [(ngModel)]="productSearch"
          />
          @if (loadingProducts) {
            <span class="loading loading-spinner loading-sm"></span>
          } @else {
            <ul
              class="max-h-40 overflow-y-auto rounded-md border border-base-content/20"
            >
              @for (product of filteredProducts; track product.id) {
                <li
                  class="cursor-pointer px-3 py-2 hover:bg-base-200"
                  [class.bg-primary]="selectedProduct?.id === product.id"
                  [class.text-primary-content]="
                    selectedProduct?.id === product.id
                  "
                  (click)="selectProduct(product)"
                >
                  {{ product.code }} - {{ product.title }} ({{
                    product.variance_code
                  }})
                </li>
              }
            </ul>
          }
        </div>
      } @else {
        <label class="form-control mb-4">
          <div class="label">
            <span class="label-text">Product code</span>
          </div>
          <input
            type="text"
            class="input input-bordered"
            maxlength="3"
            placeholder="e.g. 110"
            [(ngModel)]="customProductCode"
          />
        </label>
        <label class="form-control mb-4">
          <div class="label">
            <span class="label-text">Product title</span>
          </div>
          <input
            type="text"
            class="input input-bordered"
            placeholder="Product title"
            [(ngModel)]="customProductTitle"
          />
        </label>
      }

      <label class="form-control mb-4">
        <div class="label">
          <span class="label-text">Unique ID</span>
        </div>
        <input
          type="text"
          class="input input-bordered"
          [(ngModel)]="uniqueId"
        />
      </label>

      <label class="form-control mb-4">
        <div class="label">
          <span class="label-text">Expiration date</span>
        </div>
        <input
          type="date"
          class="input input-bordered"
          [(ngModel)]="expirationDate"
        />
      </label>

      <div class="modal-action">
        <button
          type="button"
          class="btn btn-ghost"
          (click)="closeCreateDialog()"
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          [disabled]="!canSubmit"
          (click)="create()"
        >
          @if (creating) {
            <span class="loading loading-spinner loading-sm"></span>
          }
          Create
        </button>
      </div>
    }
  </div>
  <form method="dialog" class="modal-backdrop">
    <button (click)="closeCreateDialog()">close</button>
  </form>
</dialog>
